// Fetching one mode of one scan in full.
//
// The mode pages sort across the whole result, so they need one settled array
// rather than a list of pages — which is why this is a loop behind a plain
// useQuery and not a useInfiniteQuery. Every consumer would otherwise have to
// flatten pages before it could sort them.
import type { Mode, OfferPage, OfferRow } from "./types";

// `scan_offers` caps `limit` at 1000 server-side, so five requests is five
// thousand rows. Past that the honest thing is to say so: a browser sorting a
// hundred thousand rows is not a page, it is a hang.
export const MAX_REQUESTS = 5;
export const PAGE_LIMIT = 1000;

export interface OfferBatch {
  rows: OfferRow[];
  /** True when the scan had more rows than the cap allows. */
  truncated: boolean;
}

export async function fetchAllOffers(
  get: (path: string) => Promise<OfferPage>,
  scanUid: string,
  mode: Mode,
): Promise<OfferBatch> {
  const rows: OfferRow[] = [];
  let cursor: string | null = null;

  for (let request = 0; request < MAX_REQUESTS; request += 1) {
    const base = `/scans/${encodeURIComponent(scanUid)}/offers?mode=${mode}&limit=${PAGE_LIMIT}`;
    // The cursor is base64 and carries '+', '/' and '=' — every one of which
    // means something else in a query string.
    const page: OfferPage = await get(cursor ? `${base}&cursor=${encodeURIComponent(cursor)}` : base);
    rows.push(...page.offers);
    cursor = page.next_cursor;
    if (!cursor) break;
  }

  return { rows, truncated: cursor !== null };
}
